/**
 * TaskDetailModal
 *
 * Full-detail overlay for a single task. Fetches the task by ID, renders
 * core task info (name, status, assignee, due date, description) and the
 * WatchersPanel. Opened via the onViewDetails callback on TaskRow/MyTaskRow.
 */

import styled from 'styled-components';
import { media } from '../../styles/media';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal/Modal';
import { StatusBadge } from './StatusBadge';
import { WatchersPanel } from './WatchersPanel';
import { useTask } from '../../hooks/useTasks';
import { formatLocalDate } from '../../utils/dateUtils';
import type { Task } from '../../types/tasks';

// ============================================================================
// Props
// ============================================================================

export interface TaskDetailModalProps {
  taskId: string;
  onClose: () => void;
  /** Pre-fetched task data — skips the loading state and avoids a duplicate fetch
   * when the caller has already loaded the task (e.g. MyTaskDetailPanel). */
  initialData?: Task;
}

// ============================================================================
// Styled components
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const InfoGrid = styled.dl`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  margin: 0;

  ${media.tablet} {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const InfoLabel = styled.dt`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  align-self: center;
`;

const InfoValue = styled.dd`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  align-self: center;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  margin: 0;
  white-space: pre-wrap;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin: 0;
`;

const LoadingText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.light};
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]} 0;
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]} 0;
  margin: 0;
`;

// ============================================================================
// Component
// ============================================================================

export function TaskDetailModal({ taskId, onClose, initialData }: TaskDetailModalProps) {
  const { t } = useTranslation();
  const { data: task, isLoading, isError } = useTask(taskId, initialData);

  // formatLocalDate returns "—" for null/undefined; treat that as "no due date"
  const formattedDueDate = task?.dueDate ? formatLocalDate(task.dueDate) : null;
  const assigneeName = task
    ? task.assignedTo
      ? `${task.assignedTo.firstName} ${task.assignedTo.lastName}`
      : t('tasks.detail.unassigned')
    : '';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={task?.name ?? t('tasks.detail.title')}
      size="md"
    >
      {isLoading ? (
        <LoadingText>{t('common.loading')}</LoadingText>
      ) : isError || !task ? (
        <ErrorText>{t('common.noData')}</ErrorText>
      ) : (
        <Container>
          <InfoGrid>
            <InfoLabel>{t('tasks.detail.labelStatus')}</InfoLabel>
            <InfoValue>
              <StatusBadge status={task.status} size="sm" />
            </InfoValue>

            <InfoLabel>{t('tasks.detail.labelAssignee')}</InfoLabel>
            <InfoValue>{assigneeName}</InfoValue>

            <InfoLabel>{t('tasks.detail.labelDueDate')}</InfoLabel>
            <InfoValue>
              {formattedDueDate ?? t('tasks.detail.noDueDate')}
            </InfoValue>

            <InfoLabel>{t('tasks.detail.labelDescription')}</InfoLabel>
            <InfoValue>
              {task.description ? (
                <Description>{task.description}</Description>
              ) : (
                t('tasks.detail.noDescription')
              )}
            </InfoValue>
          </InfoGrid>

          <Divider />

          <WatchersPanel
            taskId={task.id}
            clientId={task.workflowInstance?.clientMember?.id ?? null}
            assigneeUserId={task.assignedTo?.id ?? null}
          />
        </Container>
      )}
    </Modal>
  );
}

export default TaskDetailModal;
